import { Box, Card, CardHeader, CircularProgress, debounce, FormControlLabel, FormGroup, 
  InputAdornment, Skeleton, Switch, TextField, Typography, IconButton, Tooltip } from "@mui/material";
import { ListContextProvider, ReduxState, useGetMany, useGetOne, useListContext, useListController } from "ra-core";
import { ChangeEventHandler, FC, useMemo, useState } from "react";
import SearchIcon from '@/icons/Search';
import EditIcon from '@mui/icons-material/Edit';
import ReplayIcon from '@mui/icons-material/Replay';
import ClientsTable from "@/components/settings/clients/ClientsTable";
import { useSelector } from "react-redux";
import Scrollbar from "@/components/Scrollbar";
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { FunctionField } from "@/components/field";
import { RecPatch } from "@/dataProvider/tools";
import { ClientManageDto } from "@/gen/api/user";
import { ClientSettingDto, DataAnalyzerSettingDto } from "@/gen/api/securefile/api";
import CreateOrEdit from "@/controller/CreateOrEdit";
import WithSave from "@/controller/WithSave";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { Content } from "@/components/content/Content";
import { OdResourcePageProps } from "@/types/resource";
import usePermitGuard from "@/authProvider/usePermitGuard";
import { MANAGE_SECUREFILE_SETTINGS } from "@/constants";
import { EditClientScanFolders } from "./EditClientScanFolders";
import EditDMRescan from "./EditDMRescan";
import { useResourceRegister } from "@/utils/resourceRegister";


const clientsSecurefileRes = 'settings-clients-securefile'
const securefilePerClientSettingRes = 'settings-securefile-per-client'
const securefilePerClientRescanRes = 'settings-securefile-per-client/rescan'
const autoDiscoveryResource = "settings-clients-securefile-autodiscovery"


const SecureFilePerClientSetting: FC<OdResourcePageProps> = ({options}) => {
  useResourceRegister({ name: securefilePerClientRescanRes })
  const listCtrls = useListController({ 
    resource: clientsSecurefileRes, 
    basePath: '', 
    sort: defaultSort,
  })
  usePermitGuard([[MANAGE_SECUREFILE_SETTINGS]])
  return (
    <Content pageName="SecureFile Settings - Clients" resourceName="Clients" breadcrumbs={options?.breadcrumbs}>
      <ListContextProvider value={listCtrls}>
        <SecureFilePerClientSettingBody />
      </ListContextProvider>
    </Content>
  )
}

const SecureFilePerClientSettingBody: FC = () => {
  const listCtx = useListContext()
  const { ids } = listCtx
  
  const loading = useSelector<ReduxState, boolean>(store => !!store.admin.loading)

  const [states, setStates] = useState({
    term: '',
    refetchId: null as null|string,
    toEdit: null as ClientSettingDto|null,
    toRescan: null as [ClientManageDto, ClientSettingDto]|null,
    isMacOs: false as boolean|undefined
  })
  
  const debouncedSetTerm = useMemo(() => {
    const setTerm = (s:string) => {
      const v = listCtx.filterValues
      listCtx.setFilters({...v, term: s}, {})
    }
    return debounce(setTerm, 300)
  }, [listCtx])

  const handleTermChange : ChangeEventHandler<HTMLInputElement> = (e)=>{
    debouncedSetTerm(e.target.value)
    setStates(v => ({...v, term: e.target.value}))
  }

  const perClientSettingData : (ClientSettingDto | undefined)[] = (useGetMany(securefilePerClientSettingRes, ids)?.data ?? []) as any
  const refetchClientData = useGetMany(securefilePerClientSettingRes, ids)?.refetch

  const { loading: refetchingSingle } = useGetOne(
    states.refetchId == null ? 'noop' : securefilePerClientSettingRes, 
    states.refetchId ?? -1,
    useMemo(() => {
      const clearRefetch = () => setStates(v => v.refetchId ? {...v, refetchId:null} : v)
      return { onSuccess: clearRefetch, onFailure: clearRefetch }
    }, [])
  )
  const saveHandlers = useDefaultSavedHandlers("Edit SecureFile Client Setting")
  
  const rescanHandlers = useDefaultSavedHandlers("Schedule Rescan")
  const { data: fallbackSetting } = useGetOne<DataAnalyzerSettingDto&RecPatch>(autoDiscoveryResource, 'edit')

  return (
    <>
      <Card sx={{ mt: 5 }}>
        <CardHeader title={
          <Box>
            <TextField
              id="client-name-search-input"
              placeholder='Search by Client Name'
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {loading ? <CircularProgress size={20} /> : <SearchIcon fontSize="small" />}
                  </InputAdornment>
                )
              }}
              onChange={handleTermChange}
              value={states.term}
              variant="outlined"
              sx={{ width: 280, mt: 2 }}
            />
          </Box>
        } />

        <Scrollbar options={{ suppressScrollY: true }}>
          <Box sx={{ minWidth: 1150 }}>
            <ClientsTable 
              ActionField={
                <FunctionField label="Auto Discovery" sortable={false}
                  render={(d?: ClientManageDto & RecPatch) => {
                    const rawData = perClientSettingData.find(x => x?.clientId === d?.clientId)
                    const isOnMac = d?.osInfo?.toLowerCase().startsWith("unix")
                    const autoDiscoveryEnabled = rawData?.autoDiscoveryEnabled ?? false
                    return (
                      rawData === undefined ? <Skeleton variant="text" sx={{ fontSize: '1rem' }} width={100} /> :
                      <>
                        <Box>
                          <CreateOrEdit resource={securefilePerClientSettingRes} basePath="" id={rawData?.clientId ?? -1} record={rawData} {...saveHandlers} >
                            <WithSave render={({save, saving}) => (
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Switch checked={autoDiscoveryEnabled} name="autoDiscoveryEnabled" 
                                      disabled={saving || (states.refetchId === d?.clientId && refetchingSingle)}
                                      onChange={(e)=>{
                                        save({...rawData, autoDiscoveryEnabled: e.target.checked}, {
                                          onSuccess: () => setStates(v => ({...v, refetchId: d?.clientId ?? null}))
                                        })
                                      }} 
                                    />}
                                  label={
                                    <Typography variant="caption">
                                      {autoDiscoveryEnabled ? 'Enabled' : 'Disabled'}
                                    </Typography>}
                                />
                              </FormGroup>
                            )} />
                          </CreateOrEdit>
                        </Box>
                        <Box>
                          <FormGroup>
                            <FormControlLabel 
                              control={
                                <Tooltip title="Edit Scan Folders">
                                  <IconButton 
                                    aria-label="Edit Scan Folders"
                                    disabled={!autoDiscoveryEnabled}
                                    onClick={(e:any) => { 
                                      setStates(v => ({...v, toEdit: rawData || null , isMacOs: isOnMac})) 
                                    }}
                                    sx={{ 
                                      color: 'primary.main' as any, 
                                      '&:hover': { backgroundColor: 'action.hover' },
                                    }}
                                  >
                                  <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              }
                              label={
                                <Typography variant="caption">
                                  Edit Scan Folders
                                </Typography>}
                            />
                          </FormGroup>
                        </Box>
                        {autoDiscoveryEnabled &&
                          <Box>
                            <FormGroup>
                              <FormControlLabel 
                                control={
                                  <Tooltip title="Schedule Rescan">
                                    <IconButton aria-label="Schedule Rescan"
                                      sx={{ 
                                        color: 'primary.main' as any, 
                                        '&:hover': { backgroundColor: 'action.hover' },
                                      }}
                                      disabled={!autoDiscoveryEnabled}
                                      onClick={(e:any) => { 
                                        setStates(v => ({...v, toRescan: [d!, rawData] as const, isMacOs: isOnMac})) 
                                      }}
                                    >
                                      <ReplayIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                }
                                label={
                                  <Typography variant="caption">
                                    Rescan
                                  </Typography>}
                              />
                            </FormGroup>
                          </Box>}
                      </>
                    )
                  }}
                />
              }
            />
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
      </Card>
      <CreateOrEdit resource={securefilePerClientSettingRes} basePath="" id={states.toEdit?.clientId ?? -1} record={states.toEdit}
        {...saveHandlers}
        onSuccess={(data) => {
          saveHandlers.onSuccess(data)
          setStates(v => ({...v, toEdit: null}))
          refetchClientData()
        }}
      >
        {states.toEdit &&
          <EditClientScanFolders 
            open={!!states.toEdit} 
            isMac={states.isMacOs || false}
            onClose={()=>{ setStates(v => ({...v, toEdit: null })) }} 
          />
        }
      </CreateOrEdit>
      <CreateOrEdit resource={securefilePerClientRescanRes} basePath="" id={states.toRescan?.[0].clientId ?? -1} record={states.toRescan}
        {...rescanHandlers}
      >
        {states.toRescan &&
          <EditDMRescan 
            open={!!states.toRescan} 
            onClose={()=>{ setStates(v => ({...v, toRescan: null })) }} 
            fallbackOptions={(states.isMacOs ? fallbackSetting?.scanRootFoldersUnix : fallbackSetting?.scanRootFolders) ?? []}
          />}
      </CreateOrEdit>
    </>
  )
}

export default SecureFilePerClientSetting;


const defaultSort = {
  field: 'register_at',
  order: 'DESC'
}



